import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Chat as ChatIcon, Close, Send, ContentCopy, CheckCircle } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ASSISTANT_API_BASE, ASSISTANT_API_PREFIX } from '../../config/assistantApi';

const LANG_MAP = { sh: 'bash', js: 'javascript', py: 'python', curl: 'bash' };

const API_CHAT_STREAM = `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/chat/message/stream`;
const API_ESCALATE = `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/escalate/tickets`;
const API_TICKET_BY_SESSION = (sid) =>
  `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/escalate/tickets/session/${encodeURIComponent(sid)}`;
const POLL_INTERVAL_MS = 8000;
const POLL_MAX_ATTEMPTS = 60; // ~8 min

function generateSessionId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function CodeBlockWithCopy({ code, language = 'text', isUser }) {
  const [copied, setCopied] = useState(false);
  const lang = LANG_MAP[language] || language || 'text';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const label = language && language !== 'text' ? language : 'code';

  return (
    <div className="mt-2 rounded-lg overflow-hidden bg-gray-900 border border-gray-700">
      <div className="flex justify-between items-center px-3 py-1.5 bg-gray-800 border-b border-gray-700">
        <span className="text-gray-400 text-xs font-medium uppercase">{label}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-gray-300 hover:text-white text-xs transition-colors"
          aria-label="Copier le code"
        >
          {copied ? (
            <>
              <CheckCircle className="text-green-400" sx={{ fontSize: 16 }} />
              <span>Copié</span>
            </>
          ) : (
            <>
              <ContentCopy sx={{ fontSize: 16 }} />
              <span>Copier</span>
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={lang === 'text' ? 'plaintext' : lang}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '12px 16px',
          fontSize: '12px',
          lineHeight: 1.5,
          background: 'transparent',
          borderRadius: 0,
        }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
        showLineNumbers={false}
        PreTag="div"
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

function parseContentWithCode(content) {
  const parts = [];
  const regex = /```(\w*)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    const lang = match[1]?.trim() || 'text';
    const code = match[2].trim();
    if (code.length > 5) {
      parts.push({ type: 'code', value: code, language: lang });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return parts.length ? parts : [{ type: 'text', value: content }];
}

function MessageBubble({ message, isUser, onSuggestionClick }) {
  const { content, links = [], snippets = [], streaming, suggestions = [] } = message;
  const hasInlineCode = content && /```[\s\S]*?```/.test(content);
  const parts = hasInlineCode ? parseContentWithCode(content) : null;

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      role="article"
      aria-label={isUser ? 'Votre message' : 'Réponse de l\'assistant'}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? 'bg-amber-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 border border-gray-200 rounded-bl-md'
        }`}
      >
        {parts ? (
          <div className="text-sm space-y-1">
            {parts.map((p, i) =>
              p.type === 'text' ? (
                <p key={i} className="whitespace-pre-wrap break-words">
                  {p.value}
                </p>
              ) : (
                <CodeBlockWithCopy key={i} code={p.value} language={p.language} isUser={isUser} />
              )
            )}
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap break-words">
            {content}
            {streaming && <span className="inline-block w-2 h-4 ml-0.5 bg-amber-400 animate-pulse" aria-hidden="true" />}
          </p>
        )}
        {links.length > 0 && (
          <ul className="mt-2 space-y-1">
            {links.map((href, i) => (
              <li key={i}>
                <a
                  href={href}
                  className={`text-sm underline ${isUser ? 'text-amber-100' : 'text-amber-600'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {href}
                </a>
              </li>
            ))}
          </ul>
        )}
        {snippets.length > 0 && (
          <div className="mt-2 space-y-2">
            {snippets.map((s, i) => (
              <CodeBlockWithCopy
                key={i}
                code={s.code || ''}
                language={s.language || 'text'}
                isUser={isUser}
              />
            ))}
          </div>
        )}
        {!isUser && suggestions.length > 0 && onSuggestionClick && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSuggestionClick(s)}
                className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const [pageUrl, setPageUrl] = useState('');
  const [waitingForSupport, setWaitingForSupport] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const supportMessageRef = useRef(null);
  const supportEmailRef = useRef(null);
  const [supportFocus, setSupportFocus] = useState('message'); // 'email' | 'message'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setPageUrl(window.location.pathname + window.location.search || '');
  }, []);

  useEffect(() => {
    if (!supportModalOpen) return;
    const t = setTimeout(() => {
      try {
        if (supportFocus === 'email') supportEmailRef.current?.focus();
        else supportMessageRef.current?.focus();
      } catch (_) {}
    }, 50);
    return () => clearTimeout(t);
  }, [supportModalOpen, supportFocus]);

  // Polling : réponses du support (F4.2)
  useEffect(() => {
    if (!waitingForSupport || !isOpen) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      if (attempts > POLL_MAX_ATTEMPTS) {
        clearInterval(interval);
        setWaitingForSupport(false);
        return;
      }
      try {
        const res = await fetch(API_TICKET_BY_SESSION(sessionId));
        if (!res.ok) return;
        const ticket = await res.json();
        if (ticket.status === 'answered' && ticket.response_text) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: `[Support] ${ticket.response_text}`,
              links: [],
              snippets: [],
            },
          ]);
          setWaitingForSupport(false);
          clearInterval(interval);
        }
      } catch (_) {
        // continue polling
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [waitingForSupport, isOpen, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const tLower = String(text).toLowerCase();
    const isClosingIntent =
      /(merci|merci beaucoup|c'?est bon|c'est bon|au revoir|aurevoir|stop|arr[eê]te|ça suffit|ca suffit|pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower) &&
      !/(\?|comment |pourquoi |où |ou |quand |quel |quelle |cr[eée]er |payer |paiement |api |endpoint |webhook)/i.test(tLower);
    if (isClosingIntent) {
      const reply =
        /(pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower)
          ? "Parfait. On s'arrête ici. Posez une nouvelle question quand vous voulez."
          : "Avec plaisir. Si vous le souhaitez, on s'arrête ici.";
      setInput('');
      setMessages((prev) => {
        var now = Date.now();
        return [...(prev || []), { role: 'user', content: text }, { id: 'msg_' + now, role: 'assistant', content: reply, links: [], snippets: [], streaming: false }];
      });
      return;
    }

    const history = messages
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && !m.streaming)
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));

    setInput('');
    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    const assistantMsgId = `msg_${Date.now()}`;
    setMessages((prev) => [...prev, { id: assistantMsgId, role: 'assistant', content: '', links: [], snippets: [], streaming: true }]);

    try {
      const res = await fetch(API_CHAT_STREAM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: text,
          session_id: sessionId,
          page_url: pageUrl || window.location.pathname,
          history,
        }),
      });

      if (!res.ok) {
        throw new Error(`API ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              const isFinal = data.links || data.snippets || data.escalation !== undefined;

              if (isFinal) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId
                      ? {
                          ...m,
                          content: data.content !== undefined ? data.content : m.content,
                          links: data.links || [],
                          snippets: data.snippets || [],
                          suggestions: data.suggestions || [],
                          escalation: data.escalation ?? false,
                          streaming: false,
                        }
                      : m
                  )
                );
                if (data.escalation) {
                  setWaitingForSupport(true);
                  try {
                    await fetch(API_ESCALATE, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        question: text,
                        page_url: pageUrl || window.location.pathname,
                        session_id: sessionId,
                        chat_message_id: assistantMsgId,
                        email: userEmail || null,
                      }),
                    });
                  } catch (_) {}
                }
              } else if (data.content !== undefined) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId ? { ...m, content: m.content + (data.content || '') } : m
                  )
                );
              }
            } catch (_) {
              /* ignore parse errors */
            }
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) => (m.id === assistantMsgId ? { ...m, streaming: false } : m))
      );
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: "Désolé, le service est temporairement indisponible. Réessayez ou contactez le support.",
                links: [],
                snippets: [],
                streaming: false,
              }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (text) => {
    const s = String(text || '').trim();
    if (!s || loading) return;
    if (s === 'Laisser un email pour la réponse') {
      setSupportFocus('email');
      setSupportModalOpen(true);
      return;
    }
    if (s === 'Partager l’endpoint concerné' || s === "Partager l'endpoint concerné") {
      setSupportFocus('message');
      setSupportModalOpen(true);
      setSupportMessage((prev) => (String(prev || '').trim() ? prev : 'Endpoint concerné : \nErreur / payload : \n'));
      return;
    }
    if (s === 'Ajouter une ref/trans_id') {
      setSupportFocus('message');
      setSupportModalOpen(true);
      setSupportMessage((prev) => (String(prev || '').trim() ? prev : 'ref : \ntrans_id : \n'));
      return;
    }
    sendMessageWithText(s);
  };

  const createSupportTicketFromUI = async () => {
    const msg = String(supportMessage || '').trim();
    if (!msg || loading) return;
    const userMsgId = `msg_support_req_${Date.now()}`;
    setMessages((prev) => [
      ...(prev || []),
      { id: userMsgId, role: 'user', content: msg, links: [], snippets: [] },
      { id: `msg_support_ack_${Date.now()}`, role: 'assistant', content: "Je transmets votre demande à notre équipe support.\n\nLa réponse s’affichera automatiquement ici dès qu’elle sera disponible.", links: [], snippets: [] },
    ]);
    setSupportModalOpen(false);
    setSupportMessage('');
    setWaitingForSupport(true);
    try {
      await fetch(API_ESCALATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: msg,
          page_url: pageUrl || window.location.pathname,
          page_context: 'assistant',
          session_id: sessionId,
          chat_message_id: userMsgId,
          email: userEmail || null,
        }),
      });
    } catch (_) {}
  };

  const sendMessageWithText = async (textToSend) => {
    if (!textToSend?.trim() || loading) return;
    const text = String(textToSend).trim();
    const tLower = text.toLowerCase();
    const isClosingIntent =
      /(merci|merci beaucoup|c'?est bon|c'est bon|au revoir|aurevoir|stop|arr[eê]te|ça suffit|ca suffit|pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower) &&
      !/(\?|comment |pourquoi |où |ou |quand |quel |quelle |cr[eée]er |payer |paiement |api |endpoint |webhook)/i.test(tLower);
    if (isClosingIntent) {
      const reply =
        /(pour le moment|pour l'instant|suffisant|suffisante)/i.test(tLower)
          ? "Parfait, on s'arrête ici. Si vous souhaitez reprendre plus tard, posez-moi simplement une nouvelle question."
          : 'Avec plaisir ! Si vous avez une autre question, écrivez-la quand vous voulez.';
      setInput('');
      setMessages((prev) => {
        var now = Date.now();
        return [...(prev || []), { role: 'user', content: text }, { id: 'msg_' + now, role: 'assistant', content: reply, links: [], snippets: [], streaming: false }];
      });
      return;
    }
    setInput('');
    const userMsg = { role: 'user', content: textToSend };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);
    const assistantMsgId = `msg_${Date.now()}`;
    setMessages((prev) => [...prev, { id: assistantMsgId, role: 'assistant', content: '', links: [], snippets: [], streaming: true }]);
    const history = messages
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && !m.streaming)
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));
    try {
      const res = await fetch(API_CHAT_STREAM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: textToSend, session_id: sessionId, page_url: pageUrl || window.location.pathname, history }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            const isFinal = data.links || data.snippets || data.escalation !== undefined;
            if (isFinal) {
              setMessages((prev) => prev.map((m) => (m.id === assistantMsgId ? { ...m, content: data.content ?? m.content, links: data.links || [], snippets: data.snippets || [], suggestions: data.suggestions || [], escalation: data.escalation ?? false, streaming: false } : m)));
              if (data.escalation) {
                setWaitingForSupport(true);
                try {
                  await fetch(API_ESCALATE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      question: textToSend,
                      page_url: pageUrl || window.location.pathname,
                      session_id: sessionId,
                      chat_message_id: assistantMsgId,
                      email: userEmail || null,
                    }),
                  });
                } catch (_) {}
              }
            } else if (data.content !== undefined) {
              setMessages((prev) => prev.map((m) => (m.id === assistantMsgId ? { ...m, content: m.content + (data.content || '') } : m)));
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setMessages((prev) => prev.map((m) => (m.id === assistantMsgId ? { ...m, content: "Désolé, le service est temporairement indisponible.", links: [], snippets: [], streaming: false } : m)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!((e.ctrlKey || e.metaKey) && e.key === 'k')) return;
      const target = document.activeElement;
      const isTyping = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTyping) return;
      e.preventDefault();
      setIsOpen((o) => !o);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const chatContent = (
    <>
      {/* Bouton flottant - toujours visible en bas à droite */}
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[99999] w-14 h-14 rounded-full bg-amber-400 hover:bg-amber-500 text-white shadow-lg hover:shadow-xl flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 transition-all group"
        style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem' }}
        aria-label={isOpen ? 'Fermer le chat (Ctrl+K)' : 'Ouvrir l\'assistant (Ctrl+K)'}
        title={isOpen ? 'Fermer le chat (Ctrl+K)' : 'Ouvrir l\'assistant (Ctrl+K)'}
      >
        {isOpen ? <Close /> : <ChatIcon />}
        <span className="absolute -top-8 right-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
          Ctrl+K
        </span>
      </button>

      {/* Panneau chat */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[99999] w-full max-w-md h-[min(70vh,520px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ position: 'fixed', bottom: '6rem', right: '1.5rem' }}
          role="dialog"
          aria-label="Assistant Netikash"
        >
          <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-white px-4 py-3 flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="font-bold text-sm">N</span>
            </div>
            <div>
              <p className="font-semibold">Assistant Netikash</p>
              <p className="text-amber-100 text-xs">Documentation, intégration, paiements</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {messages.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">
                Posez une question sur l'API, l'authentification, les paiements ou les webhooks.
              </p>
            )}
            {messages.map((msg, i) => (
              <MessageBubble
                key={msg.id || `m${i}`}
                message={msg}
                isUser={msg.role === 'user'}
                onSuggestionClick={handleSuggestionClick}
              />
            ))}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-gray-200 text-gray-600 rounded-2xl rounded-bl-md px-4 py-2 text-sm">
                  Réflexion...
                </div>
              </div>
            )}
            {waitingForSupport && (
              <div className="flex justify-start mb-3">
                <div className="bg-amber-50 text-amber-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm border border-amber-200">
                  <div className="flex flex-col gap-1">
                    <span>Transmis au support. Réponse attendue sous 24 h ouvrées.</span>
                    <span className="text-[11px] text-amber-700/80">
                      La réponse s’affichera automatiquement ici dès qu’elle sera disponible.
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex flex-col gap-2">
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Email (optionnel) pour recevoir la réponse"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-transparent"
                aria-label="Adresse email pour la réponse (optionnel)"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSupportModalOpen(true)}
                  disabled={loading}
                  className="shrink-0 h-10 px-3 rounded-xl border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                  aria-label="Contacter le support"
                  title="Contacter le support"
                >
                  Support
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Votre question..."
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  disabled={loading}
                  aria-label="Message à l'assistant"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="rounded-xl bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white p-2.5 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  aria-label="Envoyer"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {supportModalOpen && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center px-4">
              <button
                type="button"
                className="absolute inset-0 bg-black/40"
                onClick={() => setSupportModalOpen(false)}
                aria-label="Fermer"
              />
              <div className="relative w-full max-w-lg rounded-2xl bg-white border border-gray-200 shadow-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-gray-900 font-semibold text-lg">Contacter le support</p>
                    <p className="text-gray-500 text-sm mt-1">Décris ton problème. La réponse s’affichera automatiquement ici dès qu’elle sera disponible.</p>
                  </div>
                  <button type="button" onClick={() => setSupportModalOpen(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 transition-colors" aria-label="Fermer">
                    ✕
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Email (optionnel)</label>
                    <input
                  ref={supportEmailRef}
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="ex: dev@exemple.com"
                      className="mt-2 w-full h-11 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300/60"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Message</label>
                    <textarea
                      ref={supportMessageRef}
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      rows={5}
                      placeholder="Explique le problème, l’erreur, ce que tu as déjà essayé…"
                      className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300/60"
                    />
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                  <button type="button" onClick={() => setSupportModalOpen(false)} className="h-11 px-4 rounded-xl border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                    Annuler
                  </button>
                  <button type="button" onClick={createSupportTicketFromUI} disabled={!supportMessage.trim() || loading} className="h-11 px-5 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50">
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return createPortal(chatContent, document.body);
}
